import React, { useLayoutEffect, useMemo, useRef } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { useAppSelector } from '../../hooks'
import { DEFAULT_WORKSPACE_SETTINGS } from '../../config/defaultSettings'
import { applySevvCssVariables } from '../../theme/applySevvCssVariables'
import { buildSevvMuiTheme } from '../../theme/buildSevvTheme'
import { resolveAppearanceFromWorkspace, resolveThemeTokensFromWorkspace } from '../../theme/resolveWorkspaceTheme'

/**
 * The workspace's light theme, whatever mode the rest of the app is in. Built from the same
 * tokens as the app theme, so a re-branded workspace re-colours it.
 */
export function useLightWorkspaceTheme() {
  const settings = useAppSelector((s) => s.workspace.settings) ?? DEFAULT_WORKSPACE_SETTINGS
  return useMemo(() => {
    const tokens = resolveThemeTokensFromWorkspace(settings)
    const base = resolveAppearanceFromWorkspace(settings)
    const appearance = { ...base, theme: { ...base.theme, mode: 'light' as const } }
    return { tokens, appearance, theme: buildSevvMuiTheme(tokens, appearance, false) }
  }, [settings])
}

/**
 * A light card surface for the pages around signing in (SC-438). The sign-in dialog is always
 * light, so the pages that sit next to it (email code, invite, password reset, Terms, join link,
 * guest join) stay light too, even when the workspace runs in dark mode.
 *
 * Both colour paths under it resolve to the light palette: MUI (`text.primary`, `divider`, ...)
 * through a nested theme, and `var(--sevv-*)` through variables set on a wrapper. The wrapper is
 * `display: contents`, so it adds no box to the layout; custom properties still inherit through it.
 */
export default function AuthLightSurface({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const { tokens, appearance, theme } = useLightWorkspaceTheme()

  useLayoutEffect(() => {
    if (ref.current) applySevvCssVariables(ref.current, tokens, appearance, false)
  }, [tokens, appearance])

  return (
    <ThemeProvider theme={theme}>
      <div ref={ref} data-testid="auth-light-surface" style={{ display: 'contents' }}>
        {children}
      </div>
    </ThemeProvider>
  )
}
